import { useState, useEffect } from 'react'
import { getServerSessionId } from '../../lib/serverSession'
import { getPhaseAtTimestamp } from '../../lib/phaseCutoffUtils'

interface PhaseDiscrepancy {
  paymentId: string
  transactionHash: string
  userId: number
  username: string
  email: string
  amount: number
  createdAt: string
  approvedAt: string
  activePhaseAtPayment: {
    id: number
    phase_number: number
    phase_name: string
    price_per_share: number
  }
  usedPhase: {
    id: number
    phase_number: number
    phase_name: string
    price_per_share: number
  }
  expectedShares: number
  actualShares: number
  discrepancyAmount: number
  sharePurchaseId: number | null
}

interface InvestmentPhase {
  id: number
  phase_number: number
  phase_name: string
  price_per_share: number
  start_date: string | null
  end_date: string | null
  is_active: boolean
}

interface SharePurchase {
  id: number
  transaction_reference: string
  phase_id: number
  price_per_share: number
  shares_purchased: number
}

type ScanMode = 'quick' | 'custom' | 'full'

interface PhaseFixData {
  paymentId: string
  newShares: number
  reason: string
  correctPhaseId: number
  correctPricePerShare: number
  oldPhaseId: number
  oldPricePerShare: number
  oldShares: number
  sharePurchaseId: number | null
}

interface PhaseDiscrepancyAuditProps {
  onFixShares: (fixData: PhaseFixData) => Promise<void>
}

export default function PhaseDiscrepancyAudit({ onFixShares }: PhaseDiscrepancyAuditProps) {
  const [discrepancies, setDiscrepancies] = useState<PhaseDiscrepancy[]>([])
  const [loading, setLoading] = useState(false)
  const [scanComplete, setScanComplete] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' })
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null)
  const [fixing, setFixing] = useState<string | null>(null)
  const [phases, setPhases] = useState<InvestmentPhase[]>([])

  // Filter states
  const [scanMode, setScanMode] = useState<ScanMode>('quick')
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | 'all'>('all')

  // Load phases on mount
  useEffect(() => {
    loadPhases()
  }, [])

  const loadPhases = async () => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/phase-discrepancy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({ operation: 'load_phases' })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)
      if (result.data?.phases) setPhases(result.data.phases)
    } catch (error) {
      console.error('Error loading phases:', error)
    }
  }

  const getDateRange = (): { start: string; end: string } => {
    const now = new Date()
    if (scanMode === 'quick') {
      const weekAgo = new Date(now)
      weekAgo.setDate(weekAgo.getDate() - 7)
      return { start: weekAgo.toISOString(), end: now.toISOString() }
    } else if (scanMode === 'custom') {
      return {
        start: new Date(startDate).toISOString(),
        end: new Date(endDate + 'T23:59:59').toISOString()
      }
    } else {
      // Full audit - no date filter
      return { start: '2020-01-01T00:00:00Z', end: now.toISOString() }
    }
  }

  const scanForDiscrepancies = async () => {
    setLoading(true)
    setScanComplete(false)
    setDiscrepancies([])
    setMessage(null)

    try {
      const sessionId = getServerSessionId()
      const dateRange = getDateRange()

      // Call API to scan for discrepancies
      setProgress({ current: 0, total: 0, phase: 'Scanning for discrepancies...' })

      const response = await fetch('/api/admin/phase-discrepancy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Session-Id': sessionId || '' },
        body: JSON.stringify({
          operation: 'scan_discrepancies',
          data: {
            dateRange,
            selectedPhaseId,
            phases
          }
        })
      })
      const result = await response.json()
      if (!result.success) throw new Error(result.error)

      // v4.0.60: Client-side discrepancy analysis from raw API data
      const payments = result.data?.payments || []
      const sharePurchases = result.data?.sharePurchases || []
      const totalPayments = payments.length

      setProgress({ current: totalPayments, total: totalPayments, phase: 'Analyzing phase pricing...' })

      // Build lookup: transaction_reference -> share purchase
      const spLookup: Record<string, any> = {}
      for (const sp of sharePurchases) {
        spLookup[String(sp.transaction_reference)] = sp
      }

      // Analyze each payment for phase pricing discrepancies
      const foundDiscrepancies: PhaseDiscrepancy[] = []
      for (const payment of payments) {
        const correctPhase = getPhaseAtTimestamp(payment.created_at, phases as any)
        if (!correctPhase) continue

        const sp = spLookup[String(payment.id)]
        if (!sp) continue // No share purchase record found

        const usedPricePerShare = parseFloat(sp.price_per_share)
        const correctPricePerShare = parseFloat(String(correctPhase.price_per_share))

        if (Math.abs(usedPricePerShare - correctPricePerShare) > 0.01) {
          const amount = parseFloat(payment.amount)
          const expectedShares = Math.floor(amount / correctPricePerShare)
          const actualShares = parseFloat(sp.shares_purchased)

          // Find used phase info
          const usedPhase = phases.find(p => p.id === sp.phase_id) || {
            id: sp.phase_id, phase_number: -1, phase_name: `Unknown (ID:${sp.phase_id})`, price_per_share: usedPricePerShare
          }

          foundDiscrepancies.push({
            paymentId: payment.id,
            transactionHash: payment.transaction_hash || '',
            userId: payment.user_id,
            username: payment.users?.username || '',
            email: payment.users?.email || '',
            amount,
            createdAt: payment.created_at,
            approvedAt: payment.approved_at || '',
            activePhaseAtPayment: {
              id: correctPhase.id,
              phase_number: correctPhase.phase_number,
              phase_name: correctPhase.phase_name,
              price_per_share: correctPricePerShare
            },
            usedPhase: {
              id: usedPhase.id,
              phase_number: usedPhase.phase_number,
              phase_name: usedPhase.phase_name,
              price_per_share: usedPricePerShare
            },
            expectedShares,
            actualShares,
            discrepancyAmount: actualShares - expectedShares,
            sharePurchaseId: sp.id
          })
        }
      }

      setProgress({ current: totalPayments, total: totalPayments, phase: 'Analysis complete' })
      setDiscrepancies(foundDiscrepancies)
      setScanComplete(true)

      const modeLabel = scanMode === 'quick' ? 'last 7 days' : scanMode === 'custom' ? 'selected date range' : 'all time'
      setMessage({
        type: foundDiscrepancies.length > 0 ? 'error' : 'success',
        text: foundDiscrepancies.length > 0
          ? `Found ${foundDiscrepancies.length} phase pricing discrepancies in ${totalPayments.toLocaleString()} payments (${modeLabel})`
          : `No discrepancies found in ${totalPayments.toLocaleString()} payments (${modeLabel})`
      })
    } catch (err: any) {
      console.error('Error scanning for discrepancies:', err)
      setMessage({ type: 'error', text: `Scan failed: ${err.message}` })
    } finally {
      setLoading(false)
    }
  }

  const handleFix = async (discrepancy: PhaseDiscrepancy) => {
    setFixing(discrepancy.paymentId)
    try {
      const reason = `Phase pricing correction: User paid during ${discrepancy.activePhaseAtPayment.phase_name} ($${discrepancy.activePhaseAtPayment.price_per_share}/share) but received shares calculated at ${discrepancy.usedPhase.phase_name} ($${discrepancy.usedPhase.price_per_share}/share) pricing`

      // Pass all the data needed for a complete fix
      const fixData: PhaseFixData = {
        paymentId: discrepancy.paymentId,
        newShares: discrepancy.expectedShares,
        reason,
        correctPhaseId: discrepancy.activePhaseAtPayment.id,
        correctPricePerShare: discrepancy.activePhaseAtPayment.price_per_share,
        oldPhaseId: discrepancy.usedPhase.id,
        oldPricePerShare: discrepancy.usedPhase.price_per_share,
        oldShares: discrepancy.actualShares,
        sharePurchaseId: discrepancy.sharePurchaseId
      }

      await onFixShares(fixData)
      setMessage({ type: 'success', text: `Fixed share allocation for ${discrepancy.username}` })
      // Remove from list
      setDiscrepancies(prev => prev.filter(d => d.paymentId !== discrepancy.paymentId))
    } catch (err: any) {
      setMessage({ type: 'error', text: `Fix failed: ${err.message}` })
    } finally {
      setFixing(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gray-800 rounded-xl border border-orange-500/30 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-orange-400 flex items-center gap-2">
              ⚠️ Phase Pricing Discrepancy Audit
            </h2>
            <p className="text-white text-sm mt-1">
              Scans approved payments to find cases where shares were calculated using the wrong phase pricing
            </p>
          </div>
        </div>

        {/* Scan Mode Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <button
            onClick={() => setScanMode('quick')}
            disabled={loading}
            className={`p-3 rounded-lg border-2 transition-all text-left ${
              scanMode === 'quick'
                ? 'border-green-500 bg-green-900/30'
                : 'border-gray-600 hover:border-gray-500'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xl">⚡</span>
              <div>
                <div className="font-semibold text-white">Quick Scan</div>
                <div className="text-sm text-white">Last 7 days only</div>
              </div>
            </div>
          </button>
          <button
            onClick={() => setScanMode('custom')}
            disabled={loading}
            className={`p-3 rounded-lg border-2 transition-all text-left ${
              scanMode === 'custom'
                ? 'border-blue-500 bg-blue-900/30'
                : 'border-gray-600 hover:border-gray-500'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xl">📅</span>
              <div>
                <div className="font-semibold text-white">Custom Range</div>
                <div className="text-sm text-white">Select date range</div>
              </div>
            </div>
          </button>
          <button
            onClick={() => setScanMode('full')}
            disabled={loading}
            className={`p-3 rounded-lg border-2 transition-all text-left ${
              scanMode === 'full'
                ? 'border-orange-500 bg-orange-900/30'
                : 'border-gray-600 hover:border-gray-500'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xl">🔍</span>
              <div>
                <div className="font-semibold text-white">Full Audit</div>
                <div className="text-sm text-white">All payments (slower)</div>
              </div>
            </div>
          </button>
        </div>

        {/* Custom Date Range (only shown when custom mode selected) */}
        {scanMode === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 p-3 bg-gray-900/50 rounded-lg">
            <div>
              <label className="block text-sm text-white mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm text-white mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Phase Filter */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-white">Filter by expected phase:</label>
            <select
              value={selectedPhaseId}
              onChange={(e) => setSelectedPhaseId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              disabled={loading}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Phases</option>
              {phases.map(p => (
                <option key={p.id} value={p.id}>
                  {p.phase_name} (${p.price_per_share}/share)
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Scan Button and Progress */}
        <div className="flex items-center gap-4">
          <button
            onClick={scanForDiscrepancies}
            disabled={loading}
            className="px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white rounded-lg font-medium flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Scanning...</span>
              </>
            ) : (
              <>
                🔍 Start {scanMode === 'quick' ? 'Quick' : scanMode === 'custom' ? 'Custom' : 'Full'} Scan
              </>
            )}
          </button>

          {/* Progress Indicator */}
          {loading && (
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-white">{progress.phase}</span>
                <span className="text-white font-medium">
                  {progress.total > 0 ? `${progress.current.toLocaleString()} / ${progress.total.toLocaleString()}` : 'Loading...'}
                </span>
              </div>
              {progress.total > 0 && (
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-900/30 border border-green-500/50 text-green-300' :
          message.type === 'error' ? 'bg-red-900/30 border border-red-500/50 text-red-300' :
          'bg-blue-900/30 border border-blue-500/50 text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Results */}
      {scanComplete && discrepancies.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700">
          <div className="p-3 border-b border-gray-700">
            <h3 className="font-semibold text-red-400">
              🚨 {discrepancies.length} Discrepancies Found
            </h3>
          </div>
          <div className="divide-y divide-gray-700 max-h-[600px] overflow-y-auto">
            {discrepancies.map((d) => (
              <div key={d.paymentId} className="p-4 hover:bg-gray-750">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    {/* User Info */}
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white">{d.username}</span>
                      <span className="text-gray-500 text-sm">({d.email})</span>
                    </div>
                    {/* Payment Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Amount:</span>{' '}
                        <span className="text-green-400 font-medium">${d.amount.toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Created:</span>{' '}
                        <span className="text-white">{new Date(d.createdAt).toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Approved:</span>{' '}
                        <span className="text-white">{d.approvedAt ? new Date(d.approvedAt).toLocaleString() : 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Hash:</span>{' '}
                        <span className="text-gray-300 font-mono text-sm">{d.transactionHash?.slice(0, 12)}...</span>
                      </div>
                    </div>
                    {/* Phase Discrepancy */}
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded p-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-white">Phase at Payment:</span>{' '}
                          <span className="text-green-400 font-medium">
                            {d.activePhaseAtPayment.phase_name} (${d.activePhaseAtPayment.price_per_share}/share)
                          </span>
                        </div>
                        <div>
                          <span className="text-white">Phase Used:</span>{' '}
                          <span className="text-red-400 font-medium">
                            {d.usedPhase.phase_name} (${d.usedPhase.price_per_share}/share)
                          </span>
                        </div>
                        <div>
                          <span className="text-white">Expected Shares:</span>{' '}
                          <span className="text-green-400 font-bold">{d.expectedShares.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-white">Actual Shares:</span>{' '}
                          <span className="text-red-400 font-bold">{d.actualShares.toFixed(2)}</span>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-orange-300">
                        User is owed <span className="font-bold">{d.discrepancyAmount.toFixed(2)}</span> additional shares
                      </div>
                    </div>
                  </div>
                  {/* Fix Button */}
                  <button
                    onClick={() => handleFix(d)}
                    disabled={fixing === d.paymentId}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 text-white rounded-lg font-medium flex items-center gap-2 whitespace-nowrap"
                  >
                    {fixing === d.paymentId ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <>🔧 Fix</>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No discrepancies message */}
      {scanComplete && discrepancies.length === 0 && (
        <div className="bg-gray-800 rounded-xl border border-green-500/30 p-8 text-center">
          <div className="text-4xl mb-2">✅</div>
          <p className="text-green-400 font-semibold">All Clear!</p>
          <p className="text-white text-sm">No phase pricing discrepancies found in approved payments.</p>
        </div>
      )}

      {/* Initial state */}
      {!scanComplete && !loading && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 text-center">
          <div className="text-4xl mb-2">📊</div>
          <p className="text-white">
            Select a scan mode above and click "Start Scan" to check for phase pricing discrepancies
          </p>
          <p className="text-gray-500 text-sm mt-2">
            💡 Tip: Use "Quick Scan" for recent issues, "Full Audit" to scan all historical payments
          </p>
        </div>
      )}
    </div>
  )
}
